import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import {
  Badge,
  Card,
  CardBody,
  CardFoot,
  CardHead,
  LoadError,
  PageHead,
  Seg,
  Skeleton,
  StateBlock,
} from '../components/ui.tsx'
import { SignalBadge, SignalCard } from '../components/signals.tsx'
import { usePageTitle } from '../layout/AppShell.tsx'
import { useApi } from '../lib/api.ts'
import { ago, gbpCompact, kickOffTime, toKickOff } from '../lib/format.ts'
import type { MarketListRow, MarketsPayload } from '../../shared/types.ts'

type Filter = 'all' | 'in-play' | 'pre-match' | 'held' | 'moving'
type SortKey = 'event' | 'market' | 'kickoff' | 'moving'
type Sort = { key: SortKey; dir: 'asc' | 'desc' }

/** How each column orders. Moving is by size of move, so its natural direction is descending. */
const DEFAULT_DIR: Record<SortKey, Sort['dir']> = { event: 'asc', market: 'asc', kickoff: 'asc', moving: 'desc' }

function compare(a: MarketListRow, b: MarketListRow, key: SortKey): number {
  switch (key) {
    case 'event':
      return a.event.localeCompare(b.event) || a.marketType.localeCompare(b.marketType)
    case 'market':
      return a.marketType.localeCompare(b.marketType) || a.event.localeCompare(b.event)
    case 'kickoff': {
      const at = a.startTime ? new Date(a.startTime).getTime() : Infinity
      const bt = b.startTime ? new Date(b.startTime).getTime() : Infinity
      return at - bt || a.event.localeCompare(b.event)
    }
    case 'moving':
      // Rows without a move sort last whichever way the column is pointed.
      return Math.abs(a.signal?.pct ?? 0) - Math.abs(b.signal?.pct ?? 0)
  }
}

/**
 * Browse the markets the strategies can discover.
 *
 * The list is a decision about where to look next, so every column is there to rule a row
 * in or out: how soon it starts, whether it is trading, how much is matched against it,
 * how tight the book is, and whether we are already in it. Opening a row is the only
 * action — placing anything happens on the market or the ticket, where the ladder is.
 */
export function Markets() {
  usePageTitle('Markets')
  const { data, error, loading, reload } = useApi<MarketsPayload>('/markets', 5000)
  // Moving is the default view: the table opens on what is worth a look. Typing in the
  // search box looks across every market regardless, so nothing is hidden from a search.
  const [filter, setFilter] = useState<Filter>('moving')
  const [sort, setSort] = useState<Sort>({ key: 'moving', dir: 'desc' })
  const [search, setSearch] = useState('')
  const [competition, setCompetition] = useState('all')
  const [marketType, setMarketType] = useState('all')

  const all = useMemo(() => data?.markets ?? [], [data])
  const signals = data?.signals ?? []

  const q = search.trim().toLowerCase()

  const rows = useMemo(() => {
    let list = all
    if (filter === 'in-play') list = list.filter((m) => m.inPlay)
    if (filter === 'pre-match') list = list.filter((m) => !m.inPlay)
    if (filter === 'held') list = list.filter((m) => m.held || m.workingOrders > 0)
    if (filter === 'moving' && !q) list = list.filter((m) => m.signal !== null)
    if (competition !== 'all') list = list.filter((m) => m.competition === competition)
    if (marketType !== 'all') list = list.filter((m) => m.marketType === marketType)
    if (q) {
      list = list.filter((m) =>
        [m.event, m.marketType, m.competition ?? ''].some((f) => f.toLowerCase().includes(q)),
      )
    }
    const sign = sort.dir === 'asc' ? 1 : -1
    return [...list].sort((a, b) => {
      // A move is what the Moving column is about; rows without one stay at the bottom.
      if (sort.key === 'moving' && (a.signal === null) !== (b.signal === null)) return a.signal ? -1 : 1
      return sign * compare(a, b, sort.key) || compare(a, b, 'kickoff')
    })
  }, [all, filter, competition, marketType, q, sort])

  const sortBy = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: DEFAULT_DIR[key] }))

  if (error)
    return (
      <main className="content">
        <LoadError error={error} onRetry={reload} />
      </main>
    )

  const competitions = [...new Set(all.map((m) => m.competition).filter((c): c is string => !!c))]
  const types = [...new Set(all.map((m) => m.marketType))]
  const inPlayCount = all.filter((m) => m.inPlay).length

  return (
    <main className="content">
      <PageHead
        title="Markets"
        sub="Every market our strategies can currently discover on the exchange. Open one to see the ladder, place a manual order, or read what the strategies decided there."
        actions={
          <>
            <Link className="btn outline" to="/positions">
              Positions →
            </Link>
            <Link className="btn primary" to="/bet-slip">
              Bet slip
            </Link>
          </>
        }
      />

      {/* Signals come and go on their own — the list polls, so a move that has expired
          simply is not here next time. Nothing to dismiss. */}
      {signals.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <CardHead
            title="Moving now"
            sub={`${signals.length} price${signals.length === 1 ? '' : 's'} moved a long way in a short time`}
            right={
              <button className="btn ghost sm" type="button" onClick={() => setFilter('moving')}>
                Show only these markets
              </button>
            }
          />
          <CardBody style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {signals.slice(0, 8).map((s) => (
              <SignalCard key={`${s.marketId}:${s.selectionId}:${s.kind}`} signal={s} grow />
            ))}
          </CardBody>
          {signals.length > 8 && (
            <CardFoot>
              {signals.length - 8} more below — filter the table to <b>Moving</b> to see every market with a live move.
            </CardFoot>
          )}
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <CardBody style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="input-group" style={{ minWidth: 240 }}>
            <span className="addon">⌕</span>
            <input
              className="input"
              placeholder="Search event, team or market…"
              aria-label="Search markets"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Seg
            label="Market filter"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'All' },
              { value: 'in-play', label: 'In-play' },
              { value: 'pre-match', label: 'Pre-match' },
              { value: 'held', label: 'Our exposure' },
              { value: 'moving', label: 'Moving' },
            ]}
          />
          <label className="lbl u-faint" style={{ fontSize: 'var(--fs-sm)' }} htmlFor="mkt-comp">
            Competition
          </label>
          <select
            id="mkt-comp"
            className="select"
            style={{ width: 'auto', minWidth: 170 }}
            value={competition}
            onChange={(e) => setCompetition(e.target.value)}
          >
            <option value="all">All competitions</option>
            {competitions.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
          <label className="lbl u-faint" style={{ fontSize: 'var(--fs-sm)' }} htmlFor="mkt-type">
            Market
          </label>
          <select
            id="mkt-type"
            className="select"
            style={{ width: 'auto', minWidth: 170 }}
            value={marketType}
            onChange={(e) => setMarketType(e.target.value)}
          >
            <option value="all">All market types</option>
            {types.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </CardBody>
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <CardHead
          title="Markets"
          sub={
            inPlayCount
              ? `${inPlayCount} in-play now`
              : filter === 'moving' && !q
                ? 'Only markets with a live move · search or choose All to see every market'
                : 'Football'
          }
          right={
            <span className="cell-sub">
              {loading
                ? 'loading…'
                : `${rows.length} of ${all.length} market${all.length === 1 ? '' : 's'} · prices ${ago(data?.pricesAgoS)}`}
            </span>
          }
        />
        <CardBody flush>
          {loading ? (
            <div style={{ padding: 18 }}>
              <Skeleton rows={6} />
            </div>
          ) : rows.length === 0 ? (
            <StateBlock
              title={
                all.length
                  ? filter === 'moving' && !q
                    ? 'Nothing is moving right now'
                    : 'Nothing matches these filters'
                  : 'No markets discovered'
              }
              text={
                all.length
                  ? filter === 'moving' && !q
                    ? 'No price has moved far enough, fast enough, to flag. Search above to find any market, or choose All.'
                    : 'No market matches the filters above. Clear them to see every market.'
                  : 'The engine has not reported a market for this account. Discovery runs from the engine, so this stays empty until it is running and connected to the exchange — it does not mean the exchange is empty.'
              }
              action={
                all.length ? undefined : (
                  <Link className="btn outline" to="/">
                    Check engine status
                  </Link>
                )
              }
            />
          ) : (
            <div className="table-wrap">
              <table className="tbl rows-clickable">
                <thead>
                  <tr>
                    <SortHeader label="Event" col="event" sort={sort} onSort={sortBy} />
                    <SortHeader label="Market" col="market" sort={sort} onSort={sortBy} />
                    <SortHeader label="Kick-off" col="kickoff" sort={sort} onSort={sortBy} />
                    <th>Status</th>
                    <th className="num">Matched</th>
                    <th className="num">Spread</th>
                    <SortHeader label="Moving" col="moving" sort={sort} onSort={sortBy} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => (
                    <MarketTableRow key={m.marketId} market={m} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
        <CardFoot>
          Matched is the whole exchange's volume on that market, not ours. Spread is the narrowest back/lay gap
          across its runners, in ticks — a wide spread costs more to enter and more to leave. Moving is the biggest
          live price move in the market: steam shortened, drift lengthened.
        </CardFoot>
      </Card>
    </main>
  )
}

/** A column header that sorts. The arrow says which way; the button says it is a control. */
function SortHeader({
  label,
  col,
  sort,
  onSort,
}: {
  label: string
  col: SortKey
  sort: Sort
  onSort: (key: SortKey) => void
}) {
  const active = sort.key === col
  return (
    <th aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
      <button type="button" className="th-sort" onClick={() => onSort(col)}>
        {label}
        <span className="th-arrow" aria-hidden>
          {active ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    </th>
  )
}

function MarketTableRow({ market: m }: { market: MarketListRow }) {
  const navigate = useNavigate()
  const kick = toKickOff(m.startTime)
  const open = () =>
    navigate(m.eventId ? `/games/${encodeURIComponent(m.eventId)}` : `/markets/${m.marketId}`)

  return (
    <tr
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter') open()
      }}
      tabIndex={0}
      role="link"
      aria-label={`Open ${m.event} · ${m.marketType}`}
    >
      <td>
        <div className="cell-strong">{m.event}</div>
        {m.competition && <div className="cell-sub">{m.competition}</div>}
      </td>
      <td>{m.marketType}</td>
      <td className="u-mono">
        {kickOffTime(m.startTime)}
        <div className="cell-sub">{m.inPlay && m.minute ? `${m.minute}' in-play` : kick.label}</div>
      </td>
      <td>
        {m.suspended ? (
          <Badge tone="warn" dot>
            Suspended
          </Badge>
        ) : m.inPlay ? (
          <Badge tone="warn" dot>
            In-play
          </Badge>
        ) : (
          <Badge dot>{m.status ? m.status.charAt(0) + m.status.slice(1).toLowerCase() : 'Open'}</Badge>
        )}
        {m.score && (
          <div className="cell-sub" style={{ marginTop: 3 }}>
            Score {m.score}
          </div>
        )}
      </td>
      <td className="num">{gbpCompact(m.totalMatched)}</td>
      <td className="num">
        {m.spreadTicks === null ? (
          <span className="muted">—</span>
        ) : (
          <>
            {m.spreadTicks} <span className="cell-sub">tick{m.spreadTicks === 1 ? '' : 's'}</span>
          </>
        )}
      </td>
      <td>
        {m.signal === null ? (
          <span className="muted">—</span>
        ) : (
          <>
            <SignalBadge kind={m.signal.kind} pct={m.signal.pct} />
            <div className="cell-sub" style={{ marginTop: 3 }}>
              {m.signal.runner}
            </div>
          </>
        )}
      </td>
    </tr>
  )
}
